/**
 * Ряды во времени из суточных снимков.
 *
 * Линия строится ОДНИМ способом на весь кабинет: упоминания и ответы
 * складываются по движкам, и только потом делятся. Складывать проценты нельзя —
 * движок с тремя ответами весил бы столько же, сколько движок с пятьюдесятью, —
 * а две реализации одной линии однажды нарисовали бы обзору и динамике разные
 * графики по одним и тем же данным.
 */

import type { Series, SeriesPoint } from './viz';
import type { components } from '../api/schema';

type DailySnapshot = components['schemas']['DailySnapshot'];
type CompetitorSnapshot = components['schemas']['CompetitorSnapshot'];

/** Своя линия из суточных снимков. */
export function brandSeries(rows: DailySnapshot[]): SeriesPoint[] {
  const byDate = new Map<
    string,
    { mentions: number; answers: number; complete: boolean }
  >();

  for (const row of rows) {
    const at = byDate.get(row.snapshot_date) ?? {
      mentions: 0,
      answers: 0,
      complete: true,
    };
    at.mentions += row.organic_mentions ?? 0;
    at.answers += row.organic_answers ?? 0;
    at.complete = at.complete && row.is_complete;
    byDate.set(row.snapshot_date, at);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, at]) => ({
      date,
      // Ответов не было — это разрыв, а не ноль: линия рвётся.
      value: at.answers > 0 ? (at.mentions / at.answers) * 100 : null,
      incomplete: !at.complete,
    }));
}

/** Линии соперников — по той же сетке и тем же сложением, что и своя. */
export function rivalSeriesOf(rows: CompetitorSnapshot[]): Series[] {
  const byRival = new Map<string, { name: string; rows: CompetitorSnapshot[] }>();
  for (const row of rows) {
    const at = byRival.get(row.competitor_id) ?? {
      name: row.competitor_name,
      rows: [],
    };
    at.rows.push(row);
    byRival.set(row.competitor_id, at);
  }

  return [...byRival.entries()]
    .map(([key, rival]) => {
      const byDate = new Map<string, { mentions: number; answers: number }>();
      for (const row of rival.rows) {
        const at = byDate.get(row.snapshot_date) ?? { mentions: 0, answers: 0 };
        at.mentions += row.organic_mentions ?? 0;
        at.answers += row.organic_answers ?? 0;
        byDate.set(row.snapshot_date, at);
      }
      const points = [...byDate.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, at]) => ({
          date,
          value: at.answers > 0 ? (at.mentions / at.answers) * 100 : null,
        }));
      const last = [...points].reverse().find((point) => point.value !== null);
      return { key, name: rival.name, points, last: last?.value ?? 0 };
    })
    .sort((a, b) => b.last - a.last)
    .map(({ key, name, points }) => ({ key, name, points }));
}
